/**
 * CountryAnalysis 组件类型定义
 * 对应板块: 板块 E — 在投广告数分国家
 */
import type {
  TRequestBackType,
  TRequestPieBackType,
} from '@op/utils/requestUtil';
import { countryCn, countryEn, countryEnMirror } from '@op/constants';
import { dealWithOthersName } from '@op/utils/languageValue';

// 国家 metric 类型
export type TCountryMetric = 'country_stat';

// 交叉分析指标类型
// mobile: Downloads / DAU; pc: daily dau (ampere)
export type TCrossMetric = 'download' | 'dau';

// Y轴格式类型
export type TYAxisFormat = 'integer' | 'largeNumber';

// 图表类型：line 折线、bar 柱状、pie 饼图（latest snapshot）、table 表格
export type TChartType = 'line' | 'bar' | 'pie' | 'table';

export const CROSS_METRIC_TEXT_MAP: Record<TCrossMetric, string> = {
  download: 'Download',
  dau: 'DAU',
};

// 按 entity_type 决定可选交叉指标
export function getCrossMetricsByEntityType(
  entityType?: string,
): TCrossMetric[] {
  return entityType === 'mobile' ? ['download', 'dau'] : ['dau'];
}

export const COUNTRY_SERIES_KEY_SEPARATOR = '::';

// 维度接口返回项
export interface ICountryDimensionItem {
  dimension_value: string;
  value: number;
}

export interface ICountryTimelineValue {
  dimension_value: string;
  value: number;
}

export interface ICountryTimelineItem {
  time: string;
  values: ICountryTimelineValue[];
}

// 热力图数据项
export interface IHeatmapDataItem {
  dimensionValue: string;
  alpha2Code: string;
  countryName: string;
  mapName: string;
  value: number;
}

// 交叉分析配置
export interface ICrossAnalysisConfig {
  enabled: boolean;
  country?: string;
  metric?: TCrossMetric;
}

// 组件 Props - CountryAnalysis
export interface ICountryAnalysisProps {
  filter?: {
    start_time: string;
    end_time: string;
    date_type?: 'daily' | 'weekly' | 'monthly';
  };
  gameInfo?: {
    entity_type?: 'pc' | 'mobile' | 'console';
    edition_unified_id?: string;
    id_type?: string;
  };
}

// 子组件 Props - CountryHeatmap
export interface ICountryHeatmapProps {
  data: IHeatmapDataItem[];
  loading?: boolean;
  /** 当前选中的 ECharts mapName（受控） */
  selectedMapName?: string;
  /** 地图点击回调，传出 alpha-2 countryCode 和 ECharts mapName */
  onCountryClick?: (countryCode: string, mapName: string) => void;
  /** 交叉分析配置（用于 tooltip 显示交叉指标） */
  crossAnalysis?: ICrossAnalysisConfig;
  /** 交叉指标最新值 Map: dimensionValue → value */
  crossMetricLatestMap?: Record<string, number>;
  /** 维度数据日期（用于 tooltip rightTitle） */
  dataDate?: string;
  /** 日期粒度（用于格式化 dataDate） */
  dateType?: 'daily' | 'weekly' | 'monthly';
  /** 页面筛选器（用于判断是否为单日查询，切换 tooltip 指标文案） */
  filter?: { start_time?: string; end_time?: string; [key: string]: any };
}

export interface IMapSelectionDispatchPayload {
  prevSelectedMapName?: string;
  selectedMapName?: string;
  crossAnalysisEnabled?: boolean;
}

export interface ICountryHeatmapRef {
  dispatchMapSelection: (payload: IMapSelectionDispatchPayload) => void;
}

// 子组件 Props - CountryTrendChart
export interface ICountryTrendChartProps {
  title: string;
  data: TRequestBackType;
  loading?: boolean;
  yAxisFormat?: TYAxisFormat;
  crossAnalysis?: ICrossAnalysisConfig;
  /** 饼图用 latest snapshot 数据 */
  pieData?: TRequestPieBackType;
  /** 饼图数据日期（dimension_values 接口返回的 data_date） */
  pieDataDate?: string;
  /** 过滤器（用于下载） */
  filter?: {
    start_time: string;
    end_time: string;
    date_type?: 'daily' | 'weekly' | 'monthly';
  };
  chartType: TChartType;
  setChartType: (chartType: TChartType) => void;
}

// cond 接口返回的国家项
export interface ICondCountryItem {
  country_code: string;
  country_en: string;
  country_zh: string;
}

export interface ICondRegionItem {
  region_code: string;
  region_en: string;
  region_zh: string;
  countries: ICondCountryItem[];
}

/**
 * ECharts 地图名 → countryEn 名映射（用于 countryEnMirror 反查）
 * 例: ECharts 用 "Russia"，但 countryEn 里是 "Russian"
 */
const SPECIAL_COUNTRY_NAME: Record<string, string> = {
  Russia: 'Russian',
};

/** alpha-2 → ECharts world 地图区域名（与 countryEn 不一致时需覆盖） */
const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  ae: 'United Arab Emirates',
  ba: 'Bosnia and Herz.',
  bn: 'Brunei',
  cd: 'Dem. Rep. Congo',
  cf: 'Central African Rep.',
  cg: 'Congo',
  ci: "Côte d'Ivoire",
  cv: 'Cape Verde',
  cz: 'Czech Rep.',
  do: 'Dominican Rep.',
  eh: 'W. Sahara',
  gb: 'United Kingdom',
  kp: 'Dem. Rep. Korea',
  kr: 'Korea',
  la: 'Laos',
  md: 'Moldova',
  mk: 'Macedonia',
  ps: 'Palestine',
  ru: 'Russia',
  sy: 'Syria',
  tw: 'Taiwan',
  tz: 'Tanzania',
  us: 'United States',
};

function resolveEchartsMapName(alpha2Code: string): string {
  if (!alpha2Code) return '';
  return COUNTRY_NAME_OVERRIDES[alpha2Code] || countryEn[alpha2Code] || '';
}

function isCrossMetric(value?: string): value is TCrossMetric {
  return Boolean(value && value in CROSS_METRIC_TEXT_MAP);
}

/** alpha-2 country_code 或 ECharts 地图区域名 → 小写 alpha-2 */
export function normalizeCountryCode(code?: string): string {
  if (!code) return '';

  const normalizedCode = code.trim();

  if (normalizedCode.length === 2) {
    return normalizedCode.toLowerCase();
  }

  const mirrorKey = SPECIAL_COUNTRY_NAME[normalizedCode] || normalizedCode;
  return countryEnMirror[mirrorKey] || '';
}

export function getCountryDisplayName(code?: string, isEn = true) {
  const alpha2Code = normalizeCountryCode(code);

  if (!alpha2Code) return dealWithOthersName(code || '');

  return (isEn ? countryEn[alpha2Code] : countryCn[alpha2Code]) || code || '';
}

/** country_code（alpha-2）或地图区域名 → ECharts world 地图名 */
export function getCountryMapName(code?: string) {
  if (!code) return '';

  const normalizedCode = code.trim();

  if (normalizedCode.length === 2) {
    return resolveEchartsMapName(normalizedCode.toLowerCase());
  }

  const mirrorKey = SPECIAL_COUNTRY_NAME[normalizedCode] || normalizedCode;
  if (countryEnMirror[mirrorKey]) {
    const alpha2Code = countryEnMirror[mirrorKey];
    return resolveEchartsMapName(alpha2Code) || normalizedCode;
  }

  return resolveEchartsMapName(normalizeCountryCode(normalizedCode));
}

export function buildCrossMetricSeriesKey(
  countryCode: string,
  metric: TCrossMetric,
) {
  return `${countryCode}${COUNTRY_SERIES_KEY_SEPARATOR}${metric}`;
}

export function parseCountrySeriesKey(seriesName?: string): {
  countryCode: string;
  metric?: TCrossMetric;
} {
  if (!seriesName) {
    return { countryCode: '' };
  }

  const [countryCode, metric] = seriesName.split(COUNTRY_SERIES_KEY_SEPARATOR);

  if (isCrossMetric(metric)) {
    return {
      countryCode,
      metric,
    };
  }

  return {
    countryCode: seriesName,
  };
}

export function normalizeCountryHeatmapData(
  items: ICountryDimensionItem[] = [],
  isEn = true,
): IHeatmapDataItem[] {
  return items
    .map(item => {
      const alpha2Code = normalizeCountryCode(item.dimension_value);
      const mapName = getCountryMapName(item.dimension_value);
      const countryName = getCountryDisplayName(item.dimension_value, isEn);

      return {
        dimensionValue: item.dimension_value,
        alpha2Code,
        countryName: countryName || item.dimension_value,
        mapName: mapName || countryName || item.dimension_value,
        value: item.value || 0,
      };
    })
    .filter(item => Boolean(item.mapName));
}

export function buildCountryTimelineChartData(
  timeline: ICountryTimelineItem[] = [],
  dimensionValues: string[] = [],
): TRequestBackType {
  if (!timeline.length) {
    return { series: [], xAxis: [], legend: [], isEmpty: true };
  }

  const xAxis = timeline.map(item => item.time);
  const dimensionOrder = [...dimensionValues];

  if (!dimensionOrder.length) {
    const dimensionSet = new Set<string>();

    timeline.forEach(item => {
      item.values?.forEach(valueItem => {
        if (valueItem.dimension_value) {
          dimensionSet.add(valueItem.dimension_value);
        }
      });
    });

    dimensionOrder.push(...Array.from(dimensionSet));
  }

  const series = dimensionOrder
    .map(dimensionValue => ({
      name: dimensionValue,
      data: timeline.map(item => {
        const currentValue = item.values?.find(
          valueItem => valueItem.dimension_value === dimensionValue,
        );

        return currentValue?.value ?? 0;
      }),
    }))
    .filter(item => item.data.some(value => value > 0));

  return {
    xAxis,
    legend: series.map(item => item.name),
    series,
    isEmpty: !series.length,
  };
}

/** ECharts 地图名 → country_code（alpha-2） */
export function countryNameToAlphaCode(name: string) {
  const alpha2Code = countryEnMirror[SPECIAL_COUNTRY_NAME[name] || name];
  return alpha2Code?.toUpperCase() || '';
}
